import React, {
    useEffect,
    useState
} from "react";

import {
    Coffee,
    DollarSign,
    Banknote,
    Smartphone,
    PlusCircle,
    RefreshCw
} from "lucide-react";

import {
    Link
} from "react-router-dom";

import api from "../api";

function PrestataireDashboard() {

    const [loading, setLoading] =
        useState(true);

    const [encaissements, setEncaissements] =
        useState([]);

    const [error, setError] =
        useState("");

    // ==========================================================
    // CHARGER LES ENCAISSEMENTS DU JOUR
    // ==========================================================

    const loadData = async () => {

        setLoading(true);
        setError("");

        try {

            const today =
                new Date()
                    .toISOString()
                    .split("T")[0];

            const response =
                await api.get(
                    "/blanchisserie",
                    {
                        params: {
                            date: today
                        }
                    }
                );

            const data =
                Array.isArray(
                    response.data
                )
                    ? response.data
                    : Array.isArray(
                        response.data?.data
                    )
                        ? response.data.data
                        : [];

            setEncaissements(data);

        } catch (err) {

            console.error(
                "Erreur dashboard prestataire:",
                err
            );

            setError(
                err.response?.data?.error ||
                "Impossible de charger les données."
            );

        } finally {

            setLoading(false);

        }
    };

    // ==========================================================
    // INITIALISATION
    // ==========================================================

    useEffect(() => {
        loadData();
    }, []);

    // ==========================================================
    // CALCULS
    // ==========================================================

    const totalUSD =
        encaissements
            .filter(
                item =>
                    item.devise === "USD"
            )
            .reduce(
                (
                    total,
                    item
                ) =>
                    total +
                    Number(
                        item.montant
                    ),
                0
            );

    const totalCDF =
        encaissements
            .filter(
                item =>
                    item.devise === "CDF"
            )
            .reduce(
                (
                    total,
                    item
                ) =>
                    total +
                    Number(
                        item.montant
                    ),
                0
            );

    const totalCashUSD =
        encaissements
            .filter(
                item =>
                    item.devise === "USD" &&
                    item.mode_paiement === "cash"
            )
            .reduce(
                (
                    total,
                    item
                ) =>
                    total +
                    Number(
                        item.montant
                    ),
                0
            );

    const totalMobileUSD =
        encaissements
            .filter(
                item =>
                    item.devise === "USD" &&
                    item.mode_paiement === "mobile"
            )
            .reduce(
                (
                    total,
                    item
                ) =>
                    total +
                    Number(
                        item.montant
                    ),
                0
            );

    // ==========================================================
    // FORMAT MONNAIE
    // ==========================================================

    const formatMontant = (
        montant
    ) => {
        return Number(
            montant || 0
        ).toLocaleString(
            "fr-FR",
            {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }
        );
    };

    // ==========================================================
    // FORMAT HEURE
    // ==========================================================

    const formatHeure = (
        date
    ) => {

        if (!date) {
            return "-";
        }

        return new Date(
            date
        ).toLocaleTimeString(
            "fr-FR",
            {
                hour: "2-digit",
                minute: "2-digit"
            }
        );
    };

    // ==========================================================
    // RENDER
    // ==========================================================

    return (
        <div className="p-4 md:p-6 space-y-6">

            {/* ==================================================
                EN-TÊTE
            ================================================== */}

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">

                <div>

                    <div className="flex items-center gap-2">

                        <Coffee
                            size={28}
                        />

                        <h1 className="text-2xl font-bold">
                            Prestations
                        </h1>

                    </div>

                    <p className="text-gray-500 mt-1">
                        Activité du jour
                    </p>

                </div>

                <div className="flex gap-2">

                    <button
                        type="button"
                        onClick={loadData}
                        disabled={loading}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
                    >
                        <RefreshCw
                            size={17}
                        />

                        Actualiser
                    </button>

                    <Link
                        to="/blanchisserie/nouveau"
                        className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700"
                    >
                        <PlusCircle
                            size={18}
                        />

                        Nouvel encaissement
                    </Link>

                </div>

            </div>

            {/* ==================================================
                ERREUR
            ================================================== */}

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4">
                    {error}
                </div>
            )}

            {/* ==================================================
                INDICATEURS
            ================================================== */}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">

                {/* ENCAISSEMENTS */}

                <div className="bg-white rounded-2xl shadow p-5">

                    <div className="flex items-center justify-between">

                        <div>
                            <p className="text-sm text-gray-500">
                                Encaissements
                            </p>

                            <p className="text-3xl font-bold mt-1">
                                {
                                    encaissements.length
                                }
                            </p>
                        </div>

                        <div className="p-3 rounded-xl bg-gray-100">
                            <Coffee
                                size={24}
                            />
                        </div>

                    </div>

                </div>

                {/* USD */}

                <div className="bg-white rounded-2xl shadow p-5">

                    <div className="flex items-center justify-between">

                        <div>
                            <p className="text-sm text-gray-500">
                                Chiffre d'affaires USD
                            </p>

                            <p className="text-2xl font-bold mt-1">
                                {
                                    formatMontant(
                                        totalUSD
                                    )
                                }{" "}
                                USD
                            </p>
                        </div>

                        <div className="p-3 rounded-xl bg-gray-100">
                            <DollarSign
                                size={24}
                            />
                        </div>

                    </div>

                </div>

                {/* CASH */}

                <div className="bg-white rounded-2xl shadow p-5">

                    <div className="flex items-center justify-between">

                        <div>
                            <p className="text-sm text-gray-500">
                                Espèces USD
                            </p>

                            <p className="text-2xl font-bold mt-1">
                                {
                                    formatMontant(
                                        totalCashUSD
                                    )
                                }{" "}
                                USD
                            </p>
                        </div>

                        <div className="p-3 rounded-xl bg-gray-100">
                            <Banknote
                                size={24}
                            />
                        </div>

                    </div>

                </div>

                {/* MOBILE */}

                <div className="bg-white rounded-2xl shadow p-5">

                    <div className="flex items-center justify-between">

                        <div>
                            <p className="text-sm text-gray-500">
                                Mobile Money USD
                            </p>

                            <p className="text-2xl font-bold mt-1">
                                {
                                    formatMontant(
                                        totalMobileUSD
                                    )
                                }{" "}
                                USD
                            </p>
                        </div>

                        <div className="p-3 rounded-xl bg-gray-100">
                            <Smartphone
                                size={24}
                            />
                        </div>

                    </div>

                </div>

            </div>

            {/* ==================================================
                CDF
            ================================================== */}

            {totalCDF > 0 && (
                <div className="bg-white rounded-2xl shadow p-5">

                    <p className="text-sm text-gray-500">
                        Encaissements CDF aujourd'hui
                    </p>

                    <p className="text-2xl font-bold mt-1">
                        {
                            formatMontant(
                                totalCDF
                            )
                        }{" "}
                        CDF
                    </p>

                </div>
            )}

            {/* ==================================================
                DERNIERS ENCAISSEMENTS
            ================================================== */}

            <div className="bg-white rounded-2xl shadow overflow-hidden">

                <div className="p-5 border-b">

                    <h2 className="text-lg font-bold">
                        Encaissements du jour
                    </h2>

                </div>

                {loading ? (

                    <div className="p-8 text-center text-gray-500">
                        Chargement...
                    </div>

                ) : encaissements.length === 0 ? (

                    <div className="p-8 text-center text-gray-500">

                        Aucun encaissement
                        aujourd'hui.

                        <div className="mt-4">

                            <Link
                                to="/blanchisserie/nouveau"
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700"
                            >
                                <PlusCircle
                                    size={18}
                                />

                                Enregistrer une prestation
                            </Link>

                        </div>

                    </div>

                ) : (

                    <div className="overflow-x-auto">

                        <table className="w-full">

                            <thead className="bg-gray-50">

                                <tr>

                                    <th className="text-left px-5 py-3 text-sm font-semibold">
                                        Heure
                                    </th>

                                    <th className="text-left px-5 py-3 text-sm font-semibold">
                                        N°
                                    </th>

                                    <th className="text-left px-5 py-3 text-sm font-semibold">
                                        Client
                                    </th>

                                    <th className="text-left px-5 py-3 text-sm font-semibold">
                                        Paiement
                                    </th>

                                    <th className="text-right px-5 py-3 text-sm font-semibold">
                                        Montant
                                    </th>

                                </tr>

                            </thead>

                            <tbody>

                                {encaissements
                                    .slice(0, 10)
                                    .map(
                                        (
                                            item
                                        ) => (

                                            <tr
                                                key={
                                                    item.id
                                                }
                                                className="border-t hover:bg-gray-50"
                                            >

                                                <td className="px-5 py-3 text-sm">
                                                    {
                                                        formatHeure(
                                                            item.date_encaissement
                                                        )
                                                    }
                                                </td>

                                                <td className="px-5 py-3 text-sm font-medium">

                                                    <Link
                                                        to={`/blanchisserie/${item.id}`}
                                                        className="text-blue-600 hover:underline"
                                                    >
                                                        {
                                                            item.numero
                                                        }
                                                    </Link>

                                                </td>

                                                <td className="px-5 py-3 text-sm">

                                                    {
                                                        item.client_nom ||
                                                        "Client de passage"
                                                    }

                                                </td>

                                                <td className="px-5 py-3 text-sm">

                                                    {
                                                        item.mode_paiement ===
                                                        "cash"
                                                            ? "Espèces"
                                                            : item.mode_paiement ===
                                                                "mobile"
                                                                ? "Mobile Money"
                                                                : "Carte"
                                                    }

                                                </td>

                                                <td className="px-5 py-3 text-sm text-right font-semibold">

                                                    {
                                                        formatMontant(
                                                            item.montant
                                                        )
                                                    }{" "}

                                                    {
                                                        item.devise
                                                    }

                                                </td>

                                            </tr>

                                        )
                                    )}

                            </tbody>

                        </table>

                    </div>

                )}

            </div>

        </div>
    );
}

export default PrestataireDashboard;